import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearOrderDocumentShippingAddress,
  enrichOrderRoutes,
  getOrderDocumentDetail,
  getPendingDocuments,
  getProcessedOrders,
  getSyncRun,
  saveOrderDocumentShippingAddress,
  triggerOrderControlSync,
  triggerProcessedOrderSync,
} from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import { FleetOrderModal } from "../components/fleet-orders/FleetOrderModal.jsx";
import {
  ActiveFilterSummary,
  ContextualSelectionBar,
  OrdersCommandHeader,
  OrdersFilterWorkbench,
  OrdersKpiStrip,
  OrdersListHeader,
  OrdersWorkspaceTabs,
} from "../components/orders/OrdersWorkspace.jsx";
import { RouteBadge } from "../components/RouteBadge.jsx";
import { ResponsiveFilterPanel } from "../components/ResponsiveFilterPanel.jsx";
import { SortableHeader } from "../components/SortableHeader.jsx";
import {
  ApprovalStatusBadge,
  PlanningStatusBadge,
  ProcessingStatusBadge,
} from "../components/StatusBadge.jsx";
import { SyncStatus } from "../components/SyncStatus.jsx";
import { ACTIVE_SYNC_STATUSES } from "../domain/order-status.js";
import { useSortableData } from "../hooks/useSortableData.js";

const INITIAL_FILTERS = {
  search: "",
  approvalStatus: "APPROVED",
  processingStatus: "",
  planningStatus: "UNSCHEDULED",
  route: "",
  area: "",
  unmappedOnly: false,
};

const APPROVAL_STATUS_OPTIONS = [
  {
    value: "APPROVED",
    label: "Disetujui",
  },
  {
    value: "UNAPPROVED",
    label: "Diajukan",
  },
  {
    value: "ALL",
    label: "Semua",
  },
];

const PROCESSING_STATUS_OPTIONS = [
  {
    value: "QUEUE",
    label: "Menunggu diproses",
  },
  {
    value: "WAITING",
    label: "Sebagian diproses",
  },
];

const PLANNING_STATUS_OPTIONS = [
  {
    value: "UNSCHEDULED",
    label: "Belum dijadwalkan",
  },
  {
    value: "SCHEDULED",
    label: "Sudah dijadwalkan",
  },
  {
    value: "BLOCKED",
    label: "Terblokir",
  },
];

const numberFormatter = new Intl.NumberFormat("id-ID");
const decimalFormatter = new Intl.NumberFormat("id-ID", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const ORDER_CONTROL_STALE_AFTER_MS = 10 * 60 * 1000;

function isStaleActiveSync(syncRun) {
  if (!syncRun || !ACTIVE_SYNC_STATUSES.has(syncRun.status)) return false;

  const lastProgressAt = Date.parse(
    syncRun.updated_at || syncRun.started_at || syncRun.created_at || "",
  );

  return (
    Number.isFinite(lastProgressAt) &&
    Date.now() - lastProgressAt > ORDER_CONTROL_STALE_AFTER_MS
  );
}

function formatDate(value) {
  if (!value) return "—";

  const text = String(value).trim();
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T00:00:00`)
    : new Date(text);

  if (Number.isNaN(parsed.getTime())) return text;

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  }).format(parsed);
}

function formatTimestamp(value) {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function formatNumber(value, digits = 2) {
  if (value === null || value === undefined || value === "") return "—";

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";

  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(parsed);
}

function formatQuantity(value) {
  return formatNumber(value, 2);
}

function formatWeight(value) {
  if (value === null || value === undefined) return "—";
  return `${formatNumber(value, 2)} kg`;
}

function formatVolume(value) {
  if (value === null || value === undefined) return "—";
  return `${formatNumber(value, 4)} m³`;
}

function SummaryCard({ icon, label, value, tone, detail, valueTone = "" }) {
  return (
    <article
      className={`summary-card order-kpi-card${valueTone ? ` summary-card--${valueTone}` : ""}`}
    >
      <span className={`summary-card__icon summary-card__icon--${tone}`}>
        <Icon name={icon} />
      </span>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function SlaBadge({ days }) {
  if (days === null || days === undefined) {
    return <span className="sla-badge sla-badge--unknown">—</span>;
  }

  const tone = days <= 3 ? "safe" : days <= 5 ? "warning" : "danger";

  return <span className={`sla-badge sla-badge--${tone}`}>{days} hari</span>;
}


function ProcessingLeadBadge({ days }) {
  if (days === null || days === undefined) {
    return (
      <span
        className="sla-badge sla-badge--unknown"
        title="Tanggal persetujuan atau tanggal proses historis belum tercatat lengkap"
      >
        Belum tercatat
      </span>
    );
  }

  const tone = days <= 3 ? "safe" : days <= 5 ? "warning" : "danger";
  const label = days === 0 ? "Hari yang sama" : `${days} hari`;

  return <span className={`sla-badge sla-badge--${tone}`}>{label}</span>;
}

function LoadingRows({ columns = 12 }) {
  return Array.from({ length: 8 }, (_, index) => (
    <tr className="skeleton-row" key={index}>
      {Array.from({ length: columns }, (_, cellIndex) => (
        <td key={cellIndex}>
          <span />
        </td>
      ))}
    </tr>
  ));
}

function getRouteOptionKey(route) {
  return typeof route === "string" ? route : route.code;
}

function getRouteOptionValue(route) {
  return typeof route === "string" ? route : route.code;
}

function getRouteOptionLabel(route) {
  return typeof route === "string" ? route : route.name;
}

function isSchedulableOrder(order) {
  return (
    order.approvalStatus === "APPROVED" &&
    ["QUEUE", "WAITING"].includes(order.processingStatus) &&
    order.planningStatus === "UNSCHEDULED"
  );
}

function openRowFromKeyboard(event, onOpen) {
  if (event.target !== event.currentTarget) return;
  if (!["Enter", " "].includes(event.key)) return;

  event.preventDefault();
  onOpen();
}

function ProcessedOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    withFo: 0,
    withoutFo: 0,
    cancelledFo: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    rowCount: 0,
    pageCount: 1,
  });
  const [filterOptions, setFilterOptions] = useState({ areas: [] });
  const [latestProcessedSync, setLatestProcessedSync] = useState(null);
  const [historyWindow, setHistoryWindow] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    foRelation: "ALL",
    area: "",
  });
  const [draftFilters, setDraftFilters] = useState(filters);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDocuments, setScheduleDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingProcessed, setSyncingProcessed] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [detailDocument, setDetailDocument] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPayload, setDetailPayload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [shippingSaving, setShippingSaving] = useState(false);
  const [shippingMessage, setShippingMessage] = useState("");
  const [shippingError, setShippingError] = useState("");

  const { sortedItems: sortedOrders, sort, toggleSort } = useSortableData(
    orders,
    "processedAt",
    "desc",
  );

  const loadProcessedOrders = useCallback(
    async (page = 1, nextFilters = filters) => {
      setLoading(true);
      setError("");

      try {
        const payload = await getProcessedOrders({
          page,
          pageSize: pagination.pageSize,
          search: nextFilters.search,
          foRelation: nextFilters.foRelation,
          area: nextFilters.area,
        });

        setOrders(payload.orders || []);
        setSummary(
          payload.summary || {
            total: 0,
            withFo: 0,
            withoutFo: 0,
            cancelledFo: 0,
          },
        );
        setFilterOptions(payload.filterOptions || { areas: [] });
        setLatestProcessedSync(payload.latestSync || null);
        setHistoryWindow(payload.historyWindow || null);
        setPagination(
          payload.pagination || {
            page,
            pageSize: pagination.pageSize,
            rowCount: 0,
            pageCount: 1,
          },
        );
        setSelectedIds(new Set());
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.pageSize],
  );

  useEffect(() => {
    loadProcessedOrders(1, filters);
  }, []);

  async function handleProcessedSync() {
    const searchValue = String(draftFilters.search || "").trim();
    const exactSoNumber =
      searchValue && !/\s/.test(searchValue) && /\d/.test(searchValue)
        ? searchValue
        : "";
    const nextFilters = {
      ...filters,
      search: searchValue,
    };

    setSyncingProcessed(true);
    setMessage("");
    setError("");

    try {
      const payload = await triggerProcessedOrderSync({ exactSoNumber });

      if (payload.cooldown || payload.alreadyRunning) {
        setMessage(payload.message);
        return;
      }

      const summary = payload.summary || {};
      const details = [
        `${numberFormatter.format(summary.recordsSeen || 0)} data diperiksa`,
        `${numberFormatter.format(summary.recordsNew || 0)} pesanan baru`,
        `${numberFormatter.format(summary.recordsUpdated || 0)} diperbarui`,
        `${numberFormatter.format(summary.statusChanges || 0)} perubahan status`,
      ];
      const exactLookup = summary.exactLookup;
      let exactMessage = "";

      if (exactLookup?.requestedNumber && !exactLookup.found) {
        exactMessage = ` Nomor ${exactLookup.requestedNumber} tidak ditemukan di Accurate.`;
      } else if (exactLookup?.found && !exactLookup.eligible) {
        exactMessage = ` ${exactLookup.actualNumber || exactLookup.requestedNumber} ditemukan, tetapi belum berstatus terproses dan disetujui.`;
      } else if (exactLookup?.found && exactLookup.eligible) {
        exactMessage = ` ${exactLookup.actualNumber || exactLookup.requestedNumber} berhasil diperiksa langsung.`;
      }

      setMessage(
        `${payload.message} ${details.join(" · ")}.${exactMessage} Delivery Order diperbarui di latar belakang.`,
      );
      setFilters(nextFilters);
      setDraftFilters(nextFilters);
      await loadProcessedOrders(1, nextFilters);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSyncingProcessed(false);
    }
  }

  function applyFilters(event) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const nextFilters = {
      search: String(formData.get("search") || "").trim(),
      foRelation: String(formData.get("foRelation") || "ALL"),
      area: String(formData.get("area") || ""),
    };

    setFilters(nextFilters);
    setDraftFilters(nextFilters);
    loadProcessedOrders(1, nextFilters);
  }

  function clearProcessedFilters() {
    const nextFilters = {
      search: "",
      foRelation: "ALL",
      area: "",
    };

    setFilters(nextFilters);
    setDraftFilters(nextFilters);
    loadProcessedOrders(1, nextFilters);
  }

  function changePage(page) {
    if (page < 1 || page > pagination.pageCount || loading) return;
    loadProcessedOrders(page, filters);
  }

  function isSchedulingEligible(order) {
    return order.schedulingEligible === true;
  }

  const eligibleOrders = orders.filter(isSchedulingEligible);
  const selectedOrders = orders.filter((order) => selectedIds.has(order.id));
  const allPageSelected =
    eligibleOrders.length > 0 &&
    eligibleOrders.every((order) => selectedIds.has(order.id));

  function toggleOrder(order) {
    if (!isSchedulingEligible(order)) return;

    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(order.id)) next.delete(order.id);
      else next.add(order.id);

      return next;
    });
  }

  function toggleCurrentPage() {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (allPageSelected) {
        eligibleOrders.forEach((order) => next.delete(order.id));
      } else {
        eligibleOrders.forEach((order) => next.add(order.id));
      }

      return next;
    });
  }

  async function loadProcessedOrderDetail(order, { force = false } = {}) {
    if (!order) return;

    setDetailError("");

    if (force) setDetailRefreshing(true);
    else setDetailLoading(true);

    try {
      const payload = await getOrderDocumentDetail("sales-order", order.id, {
        force,
      });
      setDetailPayload(payload);
    } catch (requestError) {
      setDetailError(requestError.message);
    } finally {
      setDetailLoading(false);
      setDetailRefreshing(false);
    }
  }

  function openDetail(order) {
    setDetailDocument(order);
    setDetailOpen(true);
    setDetailPayload(null);
    setDetailError("");
    setShippingMessage("");
    setShippingError("");
    setShippingSaving(false);
    loadProcessedOrderDetail(order);
  }

  function closeProcessedDetail() {
    setDetailOpen(false);
    setDetailDocument(null);
    setDetailPayload(null);
    setDetailError("");
    setDetailLoading(false);
    setDetailRefreshing(false);
    setShippingSaving(false);
    setShippingMessage("");
    setShippingError("");
  }

  function refreshProcessedDetail() {
    if (!detailDocument) return;
    loadProcessedOrderDetail(detailDocument, { force: true });
  }

  async function saveProcessedShippingAddress(addressId) {
    if (!detailDocument || !addressId) return;

    setShippingSaving(true);
    setShippingMessage("");
    setShippingError("");

    try {
      const payload = await saveOrderDocumentShippingAddress(
        "sales-order",
        detailDocument.id,
        addressId,
      );

      setDetailPayload((current) =>
        current
          ? {
              ...current,
              shippingAddress: payload.shippingAddress,
            }
          : current,
      );
      setShippingMessage(
        payload.message || "Alamat kirim pesanan berhasil disimpan.",
      );
      await loadProcessedOrders(pagination.page, filters);
    } catch (requestError) {
      setShippingError(requestError.message);
    } finally {
      setShippingSaving(false);
    }
  }

  async function clearProcessedShippingAddress() {
    if (!detailDocument) return;

    setShippingSaving(true);
    setShippingMessage("");
    setShippingError("");

    try {
      const payload = await clearOrderDocumentShippingAddress(
        "sales-order",
        detailDocument.id,
      );

      setDetailPayload((current) =>
        current
          ? {
              ...current,
              shippingAddress: payload.shippingAddress,
            }
          : current,
      );
      setShippingMessage(
        payload.message || "Pilihan alamat kirim berhasil dihapus.",
      );
      await loadProcessedOrders(pagination.page, filters);
    } catch (requestError) {
      setShippingError(requestError.message);
    } finally {
      setShippingSaving(false);
    }
  }

  function openScheduleModal() {
    setError("");
    setMessage("");

    if (!selectedOrders.length) return;

    const orderWithoutRoute = selectedOrders.find(
      (order) =>
        !(
          order.resolvedRouteCode ||
          order.resolvedRoute ||
          order.resolvedRouteName
        ),
    );

    if (orderWithoutRoute) {
      setError(
        "Satu atau lebih pesanan belum memiliki rute. Perbarui alamat, area, dan rute terlebih dahulu.",
      );
      return;
    }

    setScheduleDocuments(
      selectedOrders.map((order) => ({
        ...order,
        documentType: "SALES_ORDER",
        documentId: Number(order.id),
        documentKey: `SALES_ORDER:${Number(order.id)}`,
        documentNumber: order.soNumber,
      })),
    );
    setScheduleModalOpen(true);
  }

  async function handleScheduleSuccess(nextMessage) {
    setScheduleModalOpen(false);
    setSelectedIds(new Set());
    setMessage(nextMessage);
    await loadProcessedOrders(pagination.page, filters);
  }

  function relationLabel(status) {
    if (status === "WITH_FO") return "Terhubung FO";
    if (status === "CANCELLED_FO") return "FO dibatalkan/dihapus";
    return "Belum ada FO";
  }

  return (
    <div className="orders-tab-panel orders-tab-panel--processed">
      {message && <div className="notice-banner">{message}</div>}

      {error && (
        <div className="error-banner" role="alert">
          <Icon name="warning" />
          <div>
            <strong>Terjadi kendala</strong>
            <span>{error}</span>
          </div>
        </div>
      )}

      <OrdersKpiStrip label="Ringkasan Pesanan Terproses">
        <SummaryCard
          detail="Diproses dalam 30 hari terakhir"
          icon="orders"
          label="Total Terproses"
          tone="violet"
          value={numberFormatter.format(summary.total)}
        />

        <SummaryCard
          detail="Memiliki Fleet Order aktif"
          icon="route"
          label="Dengan FO"
          tone="green"
          value={numberFormatter.format(summary.withFo)}
        />

        <SummaryCard
          detail="Belum memiliki Fleet Order aktif"
          icon="warning"
          label="Tanpa FO"
          tone="amber"
          value={numberFormatter.format(summary.withoutFo)}
        />

        <SummaryCard
          detail="Pernah terkait FO batal/hapus"
          icon="partial"
          label="FO Dibatalkan"
          tone="blue"
          value={numberFormatter.format(summary.cancelledFo)}
        />
      </OrdersKpiStrip>

      <OrdersFilterWorkbench
        activeFilters={
          <ActiveFilterSummary
            labels={getProcessedFilterLabels(filters)}
            onClear={clearProcessedFilters}
            showClear={false}
          />
        }
        description="Cari dokumen dan batasi berdasarkan relasi Fleet Order atau area."
        title="Filter Riwayat"
      >
        <ResponsiveFilterPanel label="Filter Riwayat">
          <form
            aria-label="Filter Sales Order terproses"
            className="order-filters filter-panel orders-filter-form"
            onSubmit={applyFilters}
          >
            <label className="search-field orders-filter-search">
              <Icon name="search" size={17} />
              <input
                aria-label="Cari nomor SO, pelanggan, DO, atau FO"
                name="search"
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
                placeholder="Cari nomor SO, pelanggan, DO, atau FO"
                type="search"
                value={draftFilters.search}
              />
            </label>

            <div className="orders-filter-group" aria-label="Relasi Fleet Order">
              <select
                aria-label="Relasi Fleet Order"
                name="foRelation"
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    foRelation: event.target.value,
                  }))
                }
                value={draftFilters.foRelation}
              >
                <option value="ALL">Semua relasi FO</option>
                <option value="WITH_FO">Dengan FO</option>
                <option value="WITHOUT_FO">Tanpa FO</option>
                <option value="CANCELLED_FO">FO dibatalkan/dihapus</option>
              </select>
            </div>

            <div className="orders-filter-group" aria-label="Area pesanan">
              <select
                aria-label="Area"
                name="area"
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    area: event.target.value,
                  }))
                }
                value={draftFilters.area}
              >
                <option value="">Semua area</option>
                {filterOptions.areas.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </select>
            </div>

            <div className="orders-filter-actions">
              <button className="secondary-button" type="submit">
                <Icon name="filter" size={15} />
                Terapkan
              </button>

              <button
                className="text-button"
                onClick={clearProcessedFilters}
                type="button"
              >
                Bersihkan
              </button>
            </div>
          </form>
        </ResponsiveFilterPanel>
      </OrdersFilterWorkbench>

      <section className="orders-data-surface orders-data-surface--processed">
        <OrdersListHeader
          context={
            <div className="processed-sync-context">
              <span
                className="database-label"
                title={
                  historyWindow
                    ? `Periode ${historyWindow.start} sampai ${historyWindow.end}`
                    : "Riwayat 30 hari terakhir"
                }
              >
                <Icon name="sync" size={14} />
                Terakhir diperiksa · {formatTimestamp(
                  latestProcessedSync?.completed_at ||
                    latestProcessedSync?.updated_at,
                )}
              </span>

              <button
                className="secondary-button processed-sync-button"
                disabled={syncingProcessed || loading}
                onClick={handleProcessedSync}
                title="Memeriksa 100 SO terbaru per status. Jika pencarian berisi nomor SO, nomor tersebut juga dicek langsung ke Accurate."
                type="button"
              >
                <span className={syncingProcessed ? "is-spinning" : ""}>
                  <Icon name="refresh" size={16} />
                </span>
                {syncingProcessed
                  ? "Menyinkronkan…"
                  : "Sinkronkan Pesanan Terproses"}
              </button>
            </div>
          }
          description="Tinjau alamat, dokumen terkait, dan relasi Fleet Order. Sinkronisasi cepat memeriksa 100 SO terbaru per status dan nomor SO pada kolom pencarian."
          eyebrow="Riwayat Operasional"
          title="Riwayat Sales Order Terproses"
        />

        <ContextualSelectionBar
          actionDisabled={!selectedIds.size}
          actionLabel="Jadwalkan Pengiriman"
          idleText="Pilih SO yang belum terhubung ke Fleet Order aktif."
          onAction={openScheduleModal}
          onClear={() => setSelectedIds(new Set())}
          selectedCount={selectedIds.size}
          selectedNoun="SO"
        />

        <div className="table-wrap orders-data-table-wrap processed-orders-table-wrap">
          <table className="processed-orders-table responsive-card-table">
            <colgroup>
              <col className="processed-so-col--select" />
              <col className="processed-so-col--number" />
              <col className="processed-so-col--date" />
              <col className="processed-so-col--lead" />
              <col className="processed-so-col--customer" />
              <col className="processed-so-col--area" />
              <col className="processed-so-col--process" />
              <col className="processed-so-col--do" />
              <col className="processed-so-col--fo" />
              <col className="processed-so-col--team" />
              <col className="processed-so-col--vehicle" />
              <col className="processed-so-col--relation" />
            </colgroup>

            <thead>
              <tr>
                <th className="selection-cell">
                  <input
                    aria-label="Pilih semua SO terproses pada halaman ini"
                    checked={allPageSelected}
                    disabled={!eligibleOrders.length}
                    onChange={toggleCurrentPage}
                    type="checkbox"
                  />
                </th>

                <SortableHeader column="soNumber" sort={sort} toggleSort={toggleSort}>
                  Nomor Pesanan
                </SortableHeader>

                <SortableHeader column="processedAt" sort={sort} toggleSort={toggleSort}>
                  Tanggal Diproses
                </SortableHeader>

                <SortableHeader
                  column="processingLeadDays"
                  sort={sort}
                  toggleSort={toggleSort}
                >
                  Waktu Proses
                </SortableHeader>

                <SortableHeader column="customerName" sort={sort} toggleSort={toggleSort}>
                  Pelanggan
                </SortableHeader>

                <SortableHeader column="resolvedArea" sort={sort} toggleSort={toggleSort}>
                  Area
                </SortableHeader>

                <SortableHeader
                  column="processingStatus"
                  sort={sort}
                  toggleSort={toggleSort}
                >
                  Status Proses
                </SortableHeader>

                <SortableHeader column="firstDoNumber" sort={sort} toggleSort={toggleSort}>
                  Delivery Order
                </SortableHeader>

                <SortableHeader column="foNumber" sort={sort} toggleSort={toggleSort}>
                  Fleet Order
                </SortableHeader>

                <SortableHeader column="team" sort={sort} toggleSort={toggleSort}>
                  Tim
                </SortableHeader>

                <SortableHeader column="vehiclePlate" sort={sort} toggleSort={toggleSort}>
                  Armada
                </SortableHeader>

                <SortableHeader
                  column="relationStatus"
                  sort={sort}
                  toggleSort={toggleSort}
                >
                  Status Relasi
                </SortableHeader>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <LoadingRows columns={12} />
              ) : (
                sortedOrders.map((order) => (
                  <tr
                    aria-label={`Buka detail ${order.soNumber}`}
                    className="processed-order-row processed-order-row--clickable"
                    key={order.id}
                    onClick={() => openDetail(order)}
                    onKeyDown={(event) =>
                      openRowFromKeyboard(event, () => openDetail(order))
                    }
                    tabIndex={0}
                  >
                    <td
                      className="selection-cell"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        aria-label={`Pilih ${order.soNumber}`}
                        checked={selectedIds.has(order.id)}
                        disabled={!isSchedulingEligible(order)}
                        onChange={() => toggleOrder(order)}
                        title={
                          isSchedulingEligible(order)
                            ? "Pilih untuk dijadwalkan"
                            : "SO hanya dapat dijadwalkan jika tidak terhubung ke Fleet Order aktif"
                        }
                        type="checkbox"
                      />
                    </td>

                    <td>
                      <button
                        className="record-link record-link--button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openDetail(order);
                        }}
                        type="button"
                      >
                        {order.soNumber}
                      </button>
                    </td>
                    <td>{formatDate(order.processedAt) || "—"}</td>
                    <td>
                      <ProcessingLeadBadge days={order.processingLeadDays} />
                    </td>
                    <td>{order.customerName}</td>
                    <td>
                      {order.resolvedArea ? (
                        order.resolvedArea
                      ) : (
                        <span className="mapping-warning-badge">Belum dipetakan</span>
                      )}
                    </td>
                    <td>
                      <ProcessingStatusBadge
                        label={order.processingStatusLabel}
                        status={order.processingStatus}
                      />
                    </td>
                    <td>
                      {order.doCount ? (
                        <span>
                          {numberFormatter.format(order.doCount)} DO
                          {order.firstDoNumber ? ` · ${order.firstDoNumber}` : ""}
                        </span>
                      ) : (
                        "Belum ada DO"
                      )}
                    </td>
                    <td>{order.foNumber || "Belum ada FO"}</td>
                    <td>{order.team || "—"}</td>
                    <td>{order.vehiclePlate || "—"}</td>
                    <td>
                      <span
                        className={`status-badge status-badge--planning-${String(
                          order.relationStatus || "unknown",
                        ).toLowerCase()}`}
                      >
                        {relationLabel(order.relationStatus)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {!loading && orders.length === 0 && (
            <div className="empty-state">
              <span>
                <Icon name="orders" size={25} />
              </span>

              <h3>Belum ada Sales Order terproses</h3>
              <p>
                Sinkronkan data terbaru atau cari nomor SO lalu tekan Sinkronkan Pesanan Terproses.
              </p>
            </div>
          )}
        </div>

        <footer className="records-panel__footer orders-data-surface__footer">
          <span>
            Halaman {pagination.page} dari {Math.max(pagination.pageCount, 1)}
          </span>

          <div className="pagination-controls">
            <span>
              {pagination.rowCount
                ? `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.rowCount,
                  )}`
                : "0"}{" "}
              / {numberFormatter.format(pagination.rowCount)}
            </span>

            <button
              aria-label="Halaman sebelumnya"
              disabled={loading || pagination.page <= 1}
              onClick={() => changePage(pagination.page - 1)}
              type="button"
            >
              <Icon name="chevronLeft" size={16} />
            </button>

            <button
              aria-label="Halaman berikutnya"
              disabled={loading || pagination.page >= pagination.pageCount}
              onClick={() => changePage(pagination.page + 1)}
              type="button"
            >
              <Icon name="chevronRight" size={16} />
            </button>
          </div>
        </footer>
      </section>

      {detailOpen && (
        <OrderDetailModal
          detail={detailPayload}
          error={detailError}
          loading={detailLoading}
          onClearShippingAddress={clearProcessedShippingAddress}
          onClose={closeProcessedDetail}
          onRefresh={refreshProcessedDetail}
          onSaveShippingAddress={saveProcessedShippingAddress}
          refreshing={detailRefreshing}
          shippingError={shippingError}
          shippingMessage={shippingMessage}
          shippingSaving={shippingSaving}
        />
      )}

      {scheduleModalOpen && (
        <FleetOrderModal
          onCancel={() => setScheduleModalOpen(false)}
          onSuccess={handleScheduleSuccess}
          selectedDocuments={scheduleDocuments}
        />
      )}
    </div>
  );
}

function OrderDocumentDetailModal({ error, loading, onClose, payload }) {
  const document = payload?.document || payload?.order;
  const items = payload?.items || [];
  const deliveryOrders = payload?.deliveryOrders || [];
  const fleetOrders = payload?.fleetOrders || [];
  const warnings = payload?.warnings || [];

  return (
    <div className="so-detail-modal-backdrop" onClick={onClose} role="presentation">
      <section
        aria-label="Detail dokumen"
        className="so-detail-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header className="so-detail-modal__header">
          <div>
            <span className="eyebrow">Detail Pesanan</span>
            <h2>
              {document?.documentNumber || document?.soNumber || "Memuat detail…"}
            </h2>
            <p>{document?.customerName || "Mengambil detail dari database."}</p>
          </div>

          <button
            aria-label="Tutup detail dokumen"
            className="dialog-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>

        <div className="so-detail-modal__body">
          {loading && (
            <div className="detail-loading">
              <span className="is-spinning">
                <Icon name="refresh" size={18} />
              </span>
              Mengambil detail dokumen…
            </div>
          )}

          {!loading && error && (
            <div className="error-banner" role="alert">
              <Icon name="warning" />
              <div>
                <strong>Detail gagal dimuat</strong>
                <span>{error}</span>
              </div>
            </div>
          )}

          {!loading && !error && document && (
            <>
              <section className="so-detail-section">
                <div className="so-detail-grid">
                  <DetailMetric
                    label="Tanggal"
                    value={formatDate(document.documentDate || document.orderDate)}
                  />
                  <DetailMetric
                    label="Status"
                    value={
                      document.documentStatusLabel ||
                      document.processingStatusLabel ||
                      document.documentStatus ||
                      document.processingStatus ||
                      "—"
                    }
                  />
                  <DetailMetric
                    label="Area"
                    value={document.resolvedArea || "Belum tersedia"}
                  />
                  <DetailMetric
                    label="Rute"
                    value={document.resolvedRouteName || document.resolvedRouteCode || document.resolvedRoute || "Belum dipetakan"}
                  />
                </div>
              </section>

              {warnings.length > 0 && (
                <section className="so-detail-section">
                  <div className="so-warning-list">
                    {warnings.map((warning) => (
                      <span key={warning}>
                        <Icon name="warning" size={13} />
                        {warning}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              <section className="so-detail-section">
                <div className="so-detail-section__title">
                  <h3>Item</h3>
                  <span>Kuantitas awal, sudah dikirim, dan sisa</span>
                </div>

                <div className="so-detail-table-wrap">
                  <table className="so-detail-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>UOM</th>
                        <th>Kuantitas Awal</th>
                        <th>Sudah Dikirim</th>
                        <th>Sisa</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id || `${item.item_number || item.skuCode}-${item.line_index ?? item.lineIndex}`}>
                          <td>{item.item_name || item.itemName}</td>
                          <td>{item.uom_name || item.unit_name || item.unitName || "—"}</td>
                          <td>
                            {formatQuantity(item.original_quantity ?? item.originalQuantity ?? item.quantity)}
                          </td>
                          <td>
                            {formatQuantity(item.delivered_quantity ?? item.deliveredQuantity ?? 0)}
                          </td>
                          <td>
                            {formatQuantity(
                              item.remaining_quantity ?? item.remainingQuantity ?? item.quantity,
                            )}
                          </td>
                          <td>{item.quantity_status || item.quantityStatus || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {items.length === 0 && (
                    <div className="empty-state compact-empty-state">
                      Belum ada detail item tersimpan.
                    </div>
                  )}
                </div>
              </section>

              <section className="so-detail-section">
                <div className="so-detail-section__title">
                  <h3>Delivery Order</h3>
                  <span>{numberFormatter.format(deliveryOrders.length)} DO</span>
                </div>

                <div className="detail-chip-list">
                  {deliveryOrders.map((row) => (
                    <span key={row.id}>
                      {row.do_number || row.doNumber} · {formatDate(row.transaction_date || row.transactionDate)}
                    </span>
                  ))}
                  {!deliveryOrders.length && <span>Belum ada DO tersimpan</span>}
                </div>
              </section>

              <section className="so-detail-section">
                <div className="so-detail-section__title">
                  <h3>Fleet Order</h3>
                  <span>{numberFormatter.format(fleetOrders.length)} relasi</span>
                </div>

                <div className="detail-chip-list">
                  {fleetOrders.map((row) => (
                    <span key={row.id}>
                      {row.fo_number || row.foNumber} · {row.status}
                    </span>
                  ))}
                  {!fleetOrders.length && <span>Belum terhubung Fleet Order</span>}
                </div>
              </section>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function getFilterSubtitle(filters) {
  if (filters.approvalStatus === "UNAPPROVED") {
    return "Menampilkan pesanan Diajukan yang belum dapat diproses.";
  }

  if (filters.approvalStatus === "ALL") {
    return "Menampilkan pesanan Diajukan dan Disetujui yang belum selesai diproses.";
  }

  return "Menampilkan pesanan Disetujui dengan status Menunggu diproses atau Sebagian diproses.";
}

function getPendingFilterLabels(filters) {
  const labels = [];
  const approvalLabel = APPROVAL_STATUS_OPTIONS.find(
    (option) => option.value === filters.approvalStatus,
  )?.label;
  const processingLabel = PROCESSING_STATUS_OPTIONS.find(
    (option) => option.value === filters.processingStatus,
  )?.label;
  const planningLabel = PLANNING_STATUS_OPTIONS.find(
    (option) => option.value === filters.planningStatus,
  )?.label;

  if (filters.search) labels.push(`Pencarian: ${filters.search}`);
  if (approvalLabel) labels.push(`Approval: ${approvalLabel}`);
  if (processingLabel) labels.push(`Proses: ${processingLabel}`);
  if (planningLabel) labels.push(`Planning: ${planningLabel}`);
  if (filters.route) labels.push(`Rute: ${filters.route}`);
  if (filters.area) labels.push(`Area: ${filters.area}`);
  if (filters.unmappedOnly) labels.push("Belum dipetakan");

  return labels;
}

function getProcessedFilterLabels(filters) {
  const labels = [];
  const relationLabels = {
    WITH_FO: "Dengan FO",
    WITHOUT_FO: "Tanpa FO",
    CANCELLED_FO: "FO dibatalkan/dihapus",
  };

  if (filters.search) labels.push(`Pencarian: ${filters.search}`);
  if (filters.foRelation !== "ALL") {
    labels.push(`Relasi FO: ${relationLabels[filters.foRelation] || filters.foRelation}`);
  }
  if (filters.area) labels.push(`Area: ${filters.area}`);

  return labels;
}

function DetailMetric({ helper = "", label, tone = "", value }) {
  return (
    <div className={`so-detail-metric${tone ? ` so-detail-metric--${tone}` : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {helper && <small>{helper}</small>}
    </div>
  );
}

function logisticsCalculationStatusLabel(status) {
  const labels = {
    COMPLETE: "LENGKAP",
    PARTIAL: "SEBAGIAN",
    INCOMPLETE: "BELUM LENGKAP",
  };

  return labels[status] || status || "—";
}

function coordinateSourceLabel(source) {
  const labels = {
    ACCURATE: "Accurate",
    MANUAL: "Manual",
    IMPORT: "Impor",
    GEOCODED: "Geocoding",
  };

  return labels[source] || source || "Belum diketahui";
}

function CoordinateStatus({ address }) {
  const valid = address?.coordinateStatus === "VALID";
  const coordinates = valid
    ? `${formatNumber(address.latitude, 6)}, ${formatNumber(address.longitude, 6)}`
    : "Koordinat belum tersedia";
  const coordinateBadge = valid
    ? "Koordinat Alamat Kirim"
    : "Koordinat Belum Tersedia";

  return (
    <div className={`shipment-coordinate-status${valid ? " is-valid" : " is-missing"}`}>
      <span aria-hidden="true">
        <Icon name={valid ? "location" : "warning"} size={15} />
      </span>
      <div>
        <span className="shipment-coordinate-status__badge">{coordinateBadge}</span>
        <strong>{valid ? "Koordinat siap digunakan" : "Koordinat perlu dilengkapi"}</strong>
        <small>
          {coordinates} · Sumber {coordinateSourceLabel(address?.coordinateSource)}
        </small>
      </div>
    </div>
  );
}

function ShippingAddressSection({
  error,
  message,
  onClear,
  onSave,
  saving,
  shippingAddress,
}) {
  const addresses = shippingAddress?.addresses || [];
  const currentTag = shippingAddress?.currentTag || null;

  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    const currentTagId =
      currentTag?.customerShippingAddressId;

    const currentStillActive = addresses.some(
      (address) =>
        Number(address.id) === Number(currentTagId),
    );

    const nextId = currentStillActive
      ? currentTagId
      : shippingAddress?.defaultAddressId;

    setSelectedId(
      nextId ? String(nextId) : "",
    );
  }, [
    addresses,
    currentTag?.customerShippingAddressId,
    shippingAddress?.defaultAddressId,
  ]);

  const selectedAddress = addresses.find(
    (address) =>
      String(address.id) === String(selectedId),
  );
  const currentAddress = addresses.find(
    (address) =>
      Number(address.id) === Number(currentTag?.customerShippingAddressId),
  );
  const previewAddress = selectedAddress || currentAddress || currentTag;

  if (
    !shippingAddress?.hasAddressMaster &&
    !currentTag
  ) {
    return (
      <div className="shipment-address-default">
        <div>
          <strong>
            Belum ada alamat kirim khusus
          </strong>

          <p>
            Pesanan mengikuti alamat, area, dan rute default pelanggan.
          </p>
        </div>

        <span className="shipment-address-status shipment-address-status--default">
          Mengikuti Default
        </span>
      </div>
    );
  }

  const statusLabel = currentTag
    ? "Alamat Khusus"
    : shippingAddress.requiresSelection
      ? "Wajib Dipilih"
      : "1 Alamat Tersedia";

  const statusTone = currentTag
    ? "saved"
    : shippingAddress.requiresSelection
      ? "required"
      : "single";

  return (
    <div className="shipment-address-panel">
      <div className="shipment-address-panel__heading">
        <div>
          <strong>
            {shippingAddress.activeAddressCount} alamat
            aktif tersedia
          </strong>

          <p>
            Pilih alamat yang sesuai dengan
            keterangan pesanan. Pilihan disimpan per
            pesanan dan tidak hilang saat
            sinkronisasi Accurate.
          </p>
        </div>

        <span
          className={
            `shipment-address-status ` +
            `shipment-address-status--${statusTone}`
          }
        >
          {statusLabel}
        </span>
      </div>

      {message && (
        <div className="shipment-address-message">
          {message}
        </div>
      )}

      {error && (
        <div className="shipment-address-message shipment-address-message--error">
          {error}
        </div>
      )}

      {currentTag && (
        <div className="shipment-address-snapshot">
          <div className="shipment-address-snapshot__address">
            <span>Alamat terpilih</span>
            <strong>{currentTag.addressAlias}</strong>
            <p>
              {currentAddress?.addressText ||
                currentTag.addressText ||
                "Detail alamat belum tersedia."}
            </p>
          </div>

          <div>
            <span>Area Kirim</span>
            <strong>
              {currentTag.resolvedArea || "—"}
            </strong>
          </div>

          <div>
            <span>Rute</span>
            <strong>
              {currentTag.resolvedRouteName ||
                currentTag.resolvedRoute ||
                "—"}
            </strong>
          </div>

          <div>
            <span>Terakhir Disimpan</span>
            <strong>
              {formatTimestamp(currentTag.taggedAt)}
            </strong>
          </div>

          <CoordinateStatus address={currentAddress || currentTag} />
        </div>
      )}

      <div className="shipment-address-form">
        <label>
          <span>Nama Alamat</span>

          <select
            disabled={saving}
            onChange={(event) =>
              setSelectedId(event.target.value)
            }
            value={selectedId}
          >
            <option value="">
              Pilih alamat kirim
            </option>

            {addresses.map((address) => (
              <option
                key={address.id}
                value={address.id}
              >
                {address.addressAlias}
                {" · "}
                {address.resolvedArea}
                {" · "}
                {address.resolvedRouteName ||
                  address.resolvedRoute}
              </option>
            ))}
          </select>
        </label>

        <div className="shipment-address-readonly">
          <span>Area Kirim</span>
          <strong>
            {selectedAddress?.resolvedArea || "—"}
          </strong>
        </div>

        <div className="shipment-address-readonly">
          <span>Rute</span>
          <strong>
            {selectedAddress?.resolvedRouteName ||
              selectedAddress?.resolvedRoute ||
              "—"}
          </strong>
        </div>

        <div className="shipment-address-preview">
          <div>
            <span>Detail Alamat</span>
            <strong>{previewAddress?.addressAlias || "Belum dipilih"}</strong>
            <p>
              {previewAddress?.addressText ||
                "Pilih alamat untuk melihat detail tujuan pengiriman."}
            </p>
          </div>
          <CoordinateStatus address={previewAddress} />
        </div>

        <div className="shipment-address-actions">
          {currentTag && (
            <button
              className="secondary-button"
              disabled={saving}
              onClick={onClear}
              type="button"
            >
              Hapus Pilihan
            </button>
          )}

          <button
            className="primary-button"
            disabled={!selectedId || saving}
            onClick={() =>
              onSave(Number(selectedId))
            }
            type="button"
          >
            {saving
              ? "Menyimpan…"
              : "Simpan Alamat Kirim"}
          </button>
        </div>
      </div>

      {selectedAddress?.keywordText && (
        <div className="shipment-address-keyword">
          <span>Keyword master:</span>

          <strong>
            {selectedAddress.keywordText}
          </strong>
        </div>
      )}

      {currentTag &&
        !addresses.some(
          (address) =>
            Number(address.id) ===
            Number(
              currentTag.customerShippingAddressId,
            ),
        ) && (
          <div className="shipment-address-message shipment-address-message--warning">
            Alamat yang tersimpan sudah tidak aktif
            atau sudah dihapus dari Data Master.
            Pilih alamat aktif baru bila alamat SO
            perlu diperbarui.
          </div>
        )}
    </div>
  );
}

function RelatedDocumentsSection({ deliveryOrders, fleetOrders }) {
  return (
    <section className="so-detail-section so-detail-section--related">
      <div className="so-detail-section__title">
        <h3>Dokumen Terkait</h3>
        <span>Delivery Order dan riwayat Fleet Order</span>
      </div>

      <div className="so-related-documents">
        <article>
          <header>
            <span aria-hidden="true"><Icon name="document" size={16} /></span>
            <div>
              <strong>Delivery Order</strong>
              <small>{numberFormatter.format(deliveryOrders.length)} dokumen</small>
            </div>
          </header>
          <div className="so-related-documents__list">
            {deliveryOrders.map((row) => (
              <span key={row.id || row.do_number || row.doNumber}>
                <strong>{row.do_number || row.doNumber}</strong>
                <small>{formatDate(row.transaction_date || row.transactionDate)}</small>
              </span>
            ))}
            {!deliveryOrders.length && <p>Belum ada Delivery Order.</p>}
          </div>
        </article>

        <article>
          <header>
            <span aria-hidden="true"><Icon name="truck" size={16} /></span>
            <div>
              <strong>Fleet Order</strong>
              <small>{numberFormatter.format(fleetOrders.length)} relasi</small>
            </div>
          </header>
          <div className="so-related-documents__list">
            {fleetOrders.map((row) => (
              <span key={row.id || row.fo_number || row.foNumber}>
                <strong>{row.fo_number || row.foNumber}</strong>
                <small>{row.status || "Status tidak tersedia"}</small>
              </span>
            ))}
            {!fleetOrders.length && <p>Belum terhubung ke Fleet Order.</p>}
          </div>
        </article>
      </div>
    </section>
  );
}

function OrderDetailModal({
  detail,
  error,
  loading,
  onClearShippingAddress,
  onClose,
  onRefresh,
  onSaveShippingAddress,
  refreshing,
  shippingError,
  shippingMessage,
  shippingSaving,
}) {
  const order = detail?.order;
  const logistics = detail?.logistics || {};
  const items = detail?.items || [];
  const deliveryOrders = detail?.deliveryOrders || [];
  const fleetOrders = detail?.fleetOrders || [];
  const currentShippingTag =
  detail?.shippingAddress?.currentTag || null;

  const effectiveArea =
    currentShippingTag?.resolvedArea ||
    order?.resolvedArea;

  const effectiveRoute =
    currentShippingTag?.resolvedRouteName ||
    currentShippingTag?.resolvedRoute ||
    order?.resolvedRouteName ||
    order?.resolvedRouteCode ||
    order?.resolvedRoute;

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div className="so-detail-modal-backdrop" onClick={onClose} role="presentation">
      <section
        aria-label="Detail Pesanan"
        aria-modal="true"
        className="so-detail-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header className="so-detail-modal__header">
          <div>
            <span className="eyebrow">Detail Pesanan</span>
            <h2>{order?.soNumber || "Memuat detail…"}</h2>
            <p>
              {order?.customerName || "Mengambil detail pesanan dari database."}
            </p>

            {order && (
              <div className="so-detail-modal__header-meta">
                <span>
                  <Icon name="clock" size={13} />
                  {order.approvalStatus === "APPROVED"
                    ? `Disetujui ${formatDate(order.approvedAt) || "belum tercatat"}`
                    : `Pesanan ${formatDate(order.orderDate)}`}
                </span>
                <ApprovalStatusBadge
                  label={order.approvalStatusLabel}
                  status={order.approvalStatus}
                />
                <ProcessingStatusBadge
                  label={order.processingStatusLabel}
                  status={order.processingStatus}
                />
                <PlanningStatusBadge status={order.planningStatus} />
              </div>
            )}
          </div>

          <button
            aria-label="Tutup detail pesanan"
            autoFocus
            className="dialog-close"
            onClick={onClose}
            type="button"
          >
            <Icon name="close" size={19} />
          </button>
        </header>

        {loading && (
          <div className="so-detail-modal__body">
            <div className="detail-loading">
              <span className="is-spinning">
                <Icon name="refresh" size={18} />
              </span>
              Mengambil detail pesanan…
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="so-detail-modal__body">
            <div className="error-banner" role="alert">
              <Icon name="warning" />
              <div>
                <strong>Detail pesanan gagal dimuat</strong>
                <span>{error}</span>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && order && (
          <>
            <div className="so-detail-modal__body">
              <section className="so-detail-section so-detail-section--overview">
                <div className="so-detail-grid">
                  <DetailMetric
                    label="Tanggal Pesanan"
                    value={formatDate(order.orderDate)}
                  />

                  <DetailMetric
                    label="Tanggal Disetujui"
                    value={formatDate(order.approvedAt) || "Belum tercatat"}
                  />

                  {order.processedAt && (
                    <DetailMetric
                      label="Tanggal Diproses"
                      value={formatDate(order.processedAt)}
                    />
                  )}

                  <DetailMetric label="Sales" value={order.salesName || "—"} />

                  <DetailMetric
                    label="Area"
                    value={effectiveArea || "Belum tersedia"}
                  />

                  <DetailMetric
                    helper={
                      effectiveRoute
                        ? ""
                        : "Area atau rute perlu diperbarui sebelum penjadwalan."
                    }
                    label="Rute"
                    tone={effectiveRoute ? "" : "warning"}
                    value={effectiveRoute || "Belum dipetakan"}
                  />
                </div>

              </section>

              <section className="so-detail-section so-detail-section--description">
                <div className="so-detail-section__title">
                  <h3>Keterangan Pesanan</h3>
                  <span>Diambil dari keterangan dokumen Accurate</span>
                </div>

                <div className="so-description-box">
                  {order.description || "Tidak ada keterangan pada pesanan ini."}
                </div>
              </section>

              <section className="so-detail-section so-detail-section--shipping">
                <div className="so-detail-section__title">
                  <h3>Alamat Kirim</h3>

                  <span>
                    Alamat khusus hanya berlaku untuk pesanan ini
                  </span>
                </div>

                <ShippingAddressSection
                  error={shippingError}
                  message={shippingMessage}
                  onClear={onClearShippingAddress}
                  onSave={onSaveShippingAddress}
                  saving={shippingSaving}
                  shippingAddress={detail?.shippingAddress}
                />
              </section>

              <section className="so-detail-section so-detail-section--logistics">
                <div className="so-detail-section__title">
                  <h3>Ringkasan Logistik</h3>
                  <span>Status: {logisticsCalculationStatusLabel(logistics.calculationStatus)}</span>
                </div>

                <div className="so-detail-grid">
                  <DetailMetric
                    label="Total Berat"
                    value={formatWeight(logistics.totalWeightKg)}
                  />

                  <DetailMetric
                    label="Total Volume"
                    value={formatVolume(logistics.totalVolumeM3)}
                  />

                  <DetailMetric
                    label="Jumlah Item"
                    value={numberFormatter.format(items.length)}
                  />

                  <DetailMetric
                    label="Peringatan"
                    value={numberFormatter.format(logistics.warningCount || 0)}
                  />
                </div>

                {Array.isArray(logistics.warnings) &&
                  logistics.warnings.length > 0 && (
                    <div className="so-warning-list">
                      {logistics.warnings.slice(0, 5).map((warning) => (
                        <span key={warning}>
                          <Icon name="warning" size={13} />
                          {warning}
                        </span>
                      ))}

                      {logistics.warnings.length > 5 && (
                        <small>
                          +{logistics.warnings.length - 5} peringatan lainnya
                        </small>
                      )}
                    </div>
                  )}
              </section>

              <section className="so-detail-section so-detail-section--items">
                <div className="so-detail-section__title">
                  <h3>Detail Item</h3>
                  <span>Qty, UOM, berat, dan volume</span>
                </div>

                <div className="so-item-table-wrap">
                  <table className="so-item-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Nama Barang</th>
                        <th className="numeric-cell">Qty</th>
                        <th>UOM</th>
                        <th className="numeric-cell">Berat</th>
                        <th className="numeric-cell">Volume</th>
                        <th>Catatan</th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id || item.lineIndex}>
                          <td>{item.skuCode || "—"}</td>

                          <td>
                            <strong>{item.itemName}</strong>
                          </td>

                          <td className="numeric-cell">
                            {formatQuantity(item.quantity)}
                          </td>

                          <td>{item.unitName || "—"}</td>

                          <td className="numeric-cell">
                            {formatWeight(item.totalWeightKg)}
                          </td>

                          <td className="numeric-cell">
                            {formatVolume(item.totalVolumeM3)}
                          </td>

                          <td>
                            {item.warningMessage ? (
                              <span className="item-warning">
                                {item.warningMessage}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}

                      {items.length === 0 && (
                        <tr>
                          <td colSpan="7">
                            <div className="empty-inline">
                              Belum ada detail item untuk pesanan ini.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <RelatedDocumentsSection
                deliveryOrders={deliveryOrders}
                fleetOrders={fleetOrders}
              />
            </div>

            <footer className="so-detail-modal__footer">
              <span>
                Detail terakhir sinkron:{" "}
                {formatTimestamp(order.itemDetailLastSyncedAt)}
              </span>

              <div className="so-detail-modal__actions">
                <button
                  className="secondary-button"
                  onClick={onClose}
                  type="button"
                >
                  Tutup
                </button>

                <button
                  className="primary-button"
                  disabled={refreshing}
                  onClick={onRefresh}
                  type="button"
                >
                  <span className={refreshing ? "is-spinning" : ""}>
                    <Icon name="refresh" size={15} />
                  </span>
                  {refreshing ? "Mengambil ulang…" : "Ambil Ulang Detail"}
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

export function PendingOrdersPage() {
  const [activeTab, setActiveTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    pageCount: 1,
    rowCount: 0,
  });
  const [latestSync, setLatestSync] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    submitted: 0,
    all: 0,
    waiting: 0,
    partial: 0,
    unscheduled: 0,
    averageSlaDays: null,
  });
  const [validation, setValidation] = useState({
    ordersWithoutCustomerMatch: 0,
    ordersWithoutArea: 0,
    ordersWithoutRouteMapping: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    routes: [],
    areas: [],
  });
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDocument, setDetailDocument] = useState(null);
  const [detailPayload, setDetailPayload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [shippingSaving, setShippingSaving] = useState(false);
  const [shippingMessage, setShippingMessage] = useState("");
  const [shippingError, setShippingError] = useState("");

  const pollTimerRef = useRef(null);

  const { sortedItems: sortedOrders, sort, toggleSort } = useSortableData(
    orders,
    "operationalDate",
    "asc",
  );

  const loadOrders = useCallback(
    async (page = 1, activeFilters = INITIAL_FILTERS) => {
      setLoading(true);
      setError("");

      try {
        const payload = await getPendingDocuments({
          page,
          pageSize: pagination.pageSize,
          ...activeFilters,
        });

        setOrders(payload.orders || []);
        setSelectedIds(new Set());

        setSummary(
          payload.summary || {
            total: 0,
            submitted: 0,
            all: 0,
            waiting: 0,
            partial: 0,
            unscheduled: 0,
            averageSlaDays: null,
          },
        );

        setValidation(
          payload.validation || {
            ordersWithoutCustomerMatch: 0,
            ordersWithoutArea: 0,
            ordersWithoutRouteMapping: 0,
          },
        );

        setFilterOptions(
          payload.filterOptions || {
            routes: [],
            areas: [],
          },
        );

        setPagination(
          payload.pagination || {
            page,
            pageSize: pagination.pageSize,
            pageCount: 1,
            rowCount: 0,
          },
        );

        setLatestSync(payload.latestSync || null);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize],
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollSyncRun = useCallback(
    async (syncRunId) => {
      stopPolling();

      try {
        const payload = await getSyncRun(syncRunId);
        const syncRun = payload.syncRun;

        setLatestSync(syncRun);

        if (isStaleActiveSync(syncRun)) {
          setLatestSync({
            ...syncRun,
            status: "FAILED",
            error_message:
              "Sinkronisasi berhenti karena tidak ada progres lebih dari 10 menit. Silakan coba lagi.",
          });
          setSyncing(false);
          setError(
            "Sinkronisasi berhenti karena tidak ada progres lebih dari 10 menit. Silakan klik Sinkronkan Pesanan sekali lagi.",
          );
          return;
        }

        if (syncRun && ACTIVE_SYNC_STATUSES.has(syncRun.status)) {
          setSyncing(true);

          pollTimerRef.current = window.setTimeout(
            () => pollSyncRun(syncRunId),
            2000,
          );

          return;
        }

        setSyncing(false);

        setMessage(
          syncRun?.status === "SUCCESS"
            ? "Data Accurate berhasil diperbarui."
            : syncRun?.status === "PARTIAL_SUCCESS"
              ? "Sinkronisasi selesai sebagian. Periksa status sinkronisasi."
              : syncRun?.error_message ||
                "Sinkronisasi gagal. Periksa pesan kesalahan.",
        );

        await loadOrders(1, filters);
      } catch (requestError) {
        setSyncing(false);
        setError(requestError.message);
      }
    },
    [filters, loadOrders, stopPolling],
  );

  useEffect(() => {
    loadOrders(1, INITIAL_FILTERS);

    return () => {
      stopPolling();
    };
  }, [loadOrders, stopPolling]);

  useEffect(() => {
    if (latestSync && ACTIVE_SYNC_STATUSES.has(latestSync.status)) {
      setSyncing(true);
      pollSyncRun(latestSync.id);
    }
  }, [latestSync?.id, latestSync?.status, pollSyncRun]);

  async function handleManualRefresh() {
    setSyncing(true);
    setMessage("");
    setError("");

    try {
      const payload = await triggerOrderControlSync();
      setLatestSync(payload.syncRun);
      setMessage(payload.message);
    } catch (requestError) {
      setSyncing(false);
      setError(requestError.message);
    }
  }

  async function handleRouteEnrichment() {
    setEnriching(true);
    setMessage("");
    setError("");

    try {
      const payload = await enrichOrderRoutes();
      setMessage(payload.message);
      await loadOrders(1, filters);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setEnriching(false);
    }
  }

  function handleTabChange(nextTab) {
    setMessage("");
    setError("");
    setActiveTab(nextTab);
  }

  function handleFilterSubmit(event) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const nextFilters = {
      search: String(formData.get("search") || "").trim(),
      approvalStatus: String(formData.get("approvalStatus") || "APPROVED"),
      processingStatus: String(formData.get("processingStatus") || ""),
      planningStatus: String(formData.get("planningStatus") || ""),
      route: String(formData.get("route") || ""),
      area: String(formData.get("area") || ""),
      unmappedOnly: formData.get("unmappedOnly") === "on",
    };

    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    loadOrders(1, nextFilters);
  }

  function clearFilters() {
    setDraftFilters(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
    loadOrders(1, INITIAL_FILTERS);
  }

  function changePage(page) {
    if (page < 1 || page > pagination.pageCount || loading) return;
    loadOrders(page, filters);
  }

  async function loadOrderDetail(document, { force = false } = {}) {
    if (!document) return;

    setDetailError("");

    if (force) {
      setDetailRefreshing(true);
    } else {
      setDetailLoading(true);
    }

    try {
      const payload = await getOrderDocumentDetail(
        document.documentType === "REVERSE_INVOICE"
          ? "reverse-invoice"
          : "sales-order",
        document.documentId || document.id,
        { force },
      );
      setDetailPayload(payload);
    } catch (requestError) {
      setDetailError(requestError.message);
    } finally {
      setDetailLoading(false);
      setDetailRefreshing(false);
    }
  }

  function openOrderDetail(order) {
    setDetailDocument(order);
    setDetailOpen(true);
    setDetailPayload(null);
    setDetailError("");
    loadOrderDetail(order);
    setShippingMessage("");
    setShippingError("");
    setShippingSaving(false);
  }

  function closeOrderDetail() {
    setDetailOpen(false);
    setDetailDocument(null);
    setDetailPayload(null);
    setDetailError("");
    setDetailLoading(false);
    setDetailRefreshing(false);
    setShippingSaving(false);
    setShippingMessage("");
    setShippingError("");
  }

  function refreshCurrentOrderDetail() {
    if (!detailDocument) return;
    loadOrderDetail(detailDocument, { force: true });
  }

  async function saveCurrentOrderShippingAddress(addressId) {
    if (!detailDocument || !addressId) return;

    setShippingSaving(true);
    setShippingMessage("");
    setShippingError("");

    try {
      const payload =
        await saveOrderDocumentShippingAddress(
          detailDocument.documentType === "REVERSE_INVOICE"
            ? "reverse-invoice"
            : "sales-order",
          detailDocument.documentId || detailDocument.id,
          addressId,
        );

      setDetailPayload((current) =>
        current
          ? {
              ...current,
              shippingAddress: payload.shippingAddress,
            }
          : current,
      );

      setShippingMessage(
        payload.message ||
          "Alamat kirim pesanan berhasil disimpan.",
      );

      /*
      * Muat ulang daftar agar Area, Rute,
      * filter, dan validation badge langsung berubah.
      */
      await loadOrders(pagination.page, filters);
    } catch (requestError) {
      setShippingError(requestError.message);
    } finally {
      setShippingSaving(false);
    }
  }

async function clearCurrentOrderShippingAddress() {
  if (!detailDocument) return;

  setShippingSaving(true);
  setShippingMessage("");
  setShippingError("");

  try {
    const payload =
      await clearOrderDocumentShippingAddress(
        detailDocument.documentType === "REVERSE_INVOICE"
          ? "reverse-invoice"
          : "sales-order",
        detailDocument.documentId || detailDocument.id,
      );

    setDetailPayload((current) =>
      current
        ? {
            ...current,
            shippingAddress: payload.shippingAddress,
          }
        : current,
    );

    setShippingMessage(
      payload.message ||
        "Pilihan alamat kirim berhasil dihapus.",
    );

    /*
     * Setelah tag dihapus, tabel kembali memakai
     * area dan rute default dokumen.
     */
    await loadOrders(pagination.page, filters);
  } catch (requestError) {
    setShippingError(requestError.message);
  } finally {
    setShippingSaving(false);
  }
}

  const selectableOrders = orders.filter(isSchedulableOrder);

  const selectedOrders = orders.filter((order) =>
    selectedIds.has(order.documentKey || `${order.documentType}:${order.documentId || order.id}`),
  );

  const allPageSelected =
    selectableOrders.length > 0 &&
    selectableOrders.every((order) =>
      selectedIds.has(order.documentKey || `${order.documentType}:${order.documentId || order.id}`),
    );

  function toggleOrder(order) {
    if (!isSchedulableOrder(order)) return;

    setSelectedIds((current) => {
      const next = new Set(current);

      const key =
        order.documentKey ||
        `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`;

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  }

  function toggleCurrentPage() {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (allPageSelected) {
        selectableOrders.forEach((order) => {
          const key =
            order.documentKey ||
            `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`;
          next.delete(key);
        });
      } else {
        selectableOrders.forEach((order) => {
          const key =
            order.documentKey ||
            `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`;
          next.add(key);
        });
      }

      return next;
    });
  }

  function openScheduleModal() {
    setError("");

    if (!selectedOrders.length) return;

    const invalidOrder = selectedOrders.find(
      (order) => !isSchedulableOrder(order),
    );

    if (invalidOrder) {
      setError(
        "Pesanan Diajukan belum bisa dijadwalkan. Pilih hanya pesanan yang sudah Disetujui.",
      );
      return;
    }

    const routeCodes = new Set(
      selectedOrders.map(
        (order) => order.resolvedRouteCode || order.resolvedRoute || "",
      ),
    );

    if (routeCodes.has("")) {
      setError(
        "Satu atau lebih pesanan belum memiliki rute. Perbarui area dan rute terlebih dahulu.",
      );
      return;
    }

    setScheduleModalOpen(true);
  }

  async function handleScheduleSuccess(nextMessage) {
    setScheduleModalOpen(false);
    setSelectedIds(new Set());
    setMessage(nextMessage);
    await loadOrders(1, filters);
  }

  return (
    <main className="orders-control-page">
      <OrdersCommandHeader
        actions={
          <>
            {activeTab === "pending" && (
              <button
                className="primary-button orders-command-header__primary-action"
                disabled={syncing}
                onClick={handleManualRefresh}
                title="Mengambil SO aktif dan Faktur Dimuka yang belum selesai."
                type="button"
              >
                <span className={syncing ? "is-spinning" : ""}>
                  <Icon name="refresh" size={16} />
                </span>
                {syncing ? "Menyinkronkan…" : "Sinkronkan Pesanan"}
              </button>
            )}

            <button
              className="secondary-button"
              disabled={enriching}
              onClick={handleRouteEnrichment}
              type="button"
            >
              <span className={enriching ? "is-spinning" : ""}>
                <Icon name="sync" size={16} />
              </span>
              {enriching ? "Memperbarui…" : "Perbarui Area & Rute"}
            </button>
          </>
        }
        contextLabel={activeTab === "pending" ? "Perlu Diproses" : "Terproses"}
        pagination={
          activeTab === "pending" ? (
            <div className="pagination-controls">
              <span>
                {pagination.rowCount
                  ? `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(
                      pagination.page * pagination.pageSize,
                      pagination.rowCount,
                    )}`
                  : "0"}{" "}
                / {numberFormatter.format(pagination.rowCount)}
              </span>

              <button
                aria-label="Halaman sebelumnya"
                disabled={loading || pagination.page <= 1}
                onClick={() => changePage(pagination.page - 1)}
                type="button"
              >
                <Icon name="chevronLeft" size={16} />
              </button>

              <button
                aria-label="Halaman berikutnya"
                disabled={loading || pagination.page >= pagination.pageCount}
                onClick={() => changePage(pagination.page + 1)}
                type="button"
              >
                <Icon name="chevronRight" size={16} />
              </button>
            </div>
          ) : null
        }
        subtitle="Pusat kontrol pesanan yang perlu diproses dan pesanan yang sudah terproses di Accurate."
        syncStatus={<SyncStatus syncRun={latestSync} />}
        title="Pesanan"
      />

      <section className="orders-workspace-shell">
        <OrdersWorkspaceTabs activeTab={activeTab} onChange={handleTabChange} />

        {activeTab === "pending" && (
        <OrdersFilterWorkbench
          activeFilters={
            <ActiveFilterSummary
              labels={getPendingFilterLabels(filters)}
              onClear={clearFilters}
              showClear={false}
            />
          }
          description="Cari pesanan, lalu batasi status dan wilayah pengiriman."
          title="Filter Pesanan"
        >
        <ResponsiveFilterPanel label="Filter Pesanan">
          <form
            aria-label="Filter pesanan belum proses"
            className="order-filters filter-panel orders-filter-form"
            onSubmit={handleFilterSubmit}
          >
          <label className="search-field orders-filter-search">
            <Icon name="search" size={17} />

            <input
              aria-label="Cari nomor pesanan atau pelanggan"
              name="search"
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Cari nomor pesanan atau pelanggan"
              type="search"
              value={draftFilters.search}
            />
          </label>

          <div className="orders-filter-group orders-filter-group--status" aria-label="Status pesanan">
          <select
            aria-label="Status approval"
            name="approvalStatus"
            onChange={(event) => {
              const nextApprovalStatus = event.target.value;

              setDraftFilters((current) => ({
                ...current,
                approvalStatus: nextApprovalStatus,
                processingStatus:
                  nextApprovalStatus === "UNAPPROVED"
                    ? ""
                    : current.processingStatus,
              }));
            }}
            value={draftFilters.approvalStatus}
          >
            {APPROVAL_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Status proses"
            disabled={draftFilters.approvalStatus === "UNAPPROVED"}
            name="processingStatus"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                processingStatus: event.target.value,
              }))
            }
            value={draftFilters.processingStatus}
          >
            <option value="">Semua status proses</option>

            {PROCESSING_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Status planning"
            name="planningStatus"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                planningStatus: event.target.value,
              }))
            }
            value={draftFilters.planningStatus}
          >
            <option value="">Semua status planning</option>

            {PLANNING_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          </div>

          <div className="orders-filter-group orders-filter-group--location" aria-label="Wilayah pengiriman">
          <select
            aria-label="Rute"
            name="route"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                route: event.target.value,
              }))
            }
            value={draftFilters.route}
          >
            <option value="">Semua rute</option>

            {filterOptions.routes.map((route) => (
              <option
                key={getRouteOptionKey(route)}
                value={getRouteOptionValue(route)}
              >
                {getRouteOptionLabel(route)}
              </option>
            ))}
          </select>

          <select
            aria-label="Area"
            name="area"
            onChange={(event) =>
              setDraftFilters((current) => ({
                ...current,
                area: event.target.value,
              }))
            }
            value={draftFilters.area}
          >
            <option value="">Semua area</option>

            {filterOptions.areas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>

          <label className="compact-checkbox">
            <input
              checked={draftFilters.unmappedOnly}
              name="unmappedOnly"
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  unmappedOnly: event.target.checked,
                }))
              }
              type="checkbox"
            />
            Belum dipetakan
          </label>
          </div>

          <div className="orders-filter-actions">
            <button className="secondary-button" type="submit">
              <Icon name="filter" size={15} />
              Terapkan
            </button>

            <button className="text-button" onClick={clearFilters} type="button">
              Bersihkan
            </button>
          </div>
          </form>
        </ResponsiveFilterPanel>
        </OrdersFilterWorkbench>
        )}

        {activeTab === "pending" && (message || error) && (
          <div className="orders-workspace-feedback">
            {message && <div className="notice-banner">{message}</div>}

            {error && (
              <div className="error-banner" role="alert">
                <Icon name="warning" />
                <div>
                  <strong>Terjadi kendala</strong>
                  <span>{error}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "pending" ? (
        <>
        <OrdersKpiStrip label="Ringkasan Pesanan Perlu Diproses">
          <SummaryCard
            detail="APPROVED + QUEUE / WAITING"
            icon="orders"
            label="Pesanan Perlu Diproses"
            tone="violet"
            value={numberFormatter.format(summary.total)}
          />

          <SummaryCard
            detail="UNAPPROVED"
            icon="orders"
            label="Pesanan Diajukan"
            tone="violet"
            value={numberFormatter.format(summary.submitted || 0)}
          />

          <SummaryCard
            detail="Status: Menunggu diproses"
            icon="clock"
            label="Menunggu diproses"
            tone="blue"
            value={numberFormatter.format(summary.waiting)}
          />

          <SummaryCard
            detail="Status: Sebagian diproses"
            icon="partial"
            label="Sebagian diproses"
            tone="amber"
            value={numberFormatter.format(summary.partial)}
          />

          <SummaryCard
            detail="Belum masuk Fleet Order aktif"
            icon="database"
            label="Belum dijadwalkan"
            tone="green"
            value={numberFormatter.format(summary.unscheduled)}
          />

          <SummaryCard
            detail="Target ≤ 3 hari · dihitung sejak disetujui"
            icon="clock"
            label="Rata-rata SLA"
            tone="violet"
            valueTone={
              summary.averageSlaDays === null
                ? "neutral"
                : summary.averageSlaDays <= 3
                  ? "good"
                  : "danger"
            }
            value={
              summary.averageSlaDays === null
                ? "—"
                : `${decimalFormatter.format(summary.averageSlaDays)} hari`
            }
          />
        </OrdersKpiStrip>

        <section className="orders-data-surface orders-data-surface--pending">
          <OrdersListHeader
            context={
              <span className="database-label">
                <Icon name="database" size={14} />
                Sumber: Database Route Planning
              </span>
            }
            description={getFilterSubtitle(filters)}
            eyebrow="Daftar Operasional"
            title="Daftar Pesanan Perlu Diproses"
          >
            <span className="validation-summary">
                {validation.ordersWithoutCustomerMatch > 0 && (
                  <span className="validation-indicator">
                    <Icon name="warning" size={14} />
                    {numberFormatter.format(
                      validation.ordersWithoutCustomerMatch,
                    )}{" "}
                    tanpa pelanggan
                  </span>
                )}

                {validation.ordersWithoutArea > 0 && (
                  <span className="validation-indicator">
                    <Icon name="warning" size={14} />
                    {numberFormatter.format(validation.ordersWithoutArea)} tanpa
                    area
                  </span>
                )}

                {validation.ordersWithoutRouteMapping > 0 && (
                  <span className="validation-indicator">
                    <Icon name="warning" size={14} />
                    {numberFormatter.format(
                      validation.ordersWithoutRouteMapping,
                    )}{" "}
                    belum dipetakan
                  </span>
                )}
            </span>
          </OrdersListHeader>

          <ContextualSelectionBar
            actionDisabled={selectedIds.size === 0}
            actionLabel="Jadwalkan Pengiriman"
            idleText="Pilih pesanan yang disetujui untuk memulai penjadwalan pengiriman."
            onAction={openScheduleModal}
            onClear={() => setSelectedIds(new Set())}
            selectedCount={selectedIds.size}
          />

          <div className="table-wrap pending-so-table-wrap orders-data-table-wrap">
            <table className="pending-so-table responsive-card-table">
              <colgroup>
                <col className="pending-so-col--select" />
                <col className="pending-so-col--number" />
                <col className="pending-so-col--date" />
                <col className="pending-so-col--customer" />
                <col className="pending-so-col--area" />
                <col className="pending-so-col--route" />
                <col className="pending-so-col--sales" />
                <col className="pending-so-col--approval" />
                <col className="pending-so-col--process" />
                <col className="pending-so-col--planning" />
                <col className="pending-so-col--sla" />
                <col className="pending-so-col--sync" />
              </colgroup>

              <thead>
                <tr>
                  <th className="selection-cell">
                    <input
                      aria-label="Pilih semua pesanan pada halaman ini"
                      checked={allPageSelected}
                      disabled={selectableOrders.length === 0}
                      onChange={toggleCurrentPage}
                      type="checkbox"
                    />
                  </th>

                  <SortableHeader
                    column="documentNumber"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Nomor Pesanan
                  </SortableHeader>

                  <SortableHeader
                    column="operationalDate"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    {filters.approvalStatus === "UNAPPROVED"
                      ? "Tanggal Pesanan"
                      : "Tanggal Disetujui"}
                  </SortableHeader>

                  <SortableHeader
                    column="customerName"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Pelanggan
                  </SortableHeader>

                  <SortableHeader
                    column="resolvedArea"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Area
                  </SortableHeader>

                  <SortableHeader
                    column="resolvedRouteName"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Rute
                  </SortableHeader>

                  <SortableHeader
                    column="salesName"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Sales
                  </SortableHeader>

                  <SortableHeader
                    column="approvalStatus"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Status Approval
                  </SortableHeader>

                  <SortableHeader
                    column="processingStatus"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Status Proses
                  </SortableHeader>

                  <SortableHeader
                    column="planningStatus"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Status Planning
                  </SortableHeader>

                  <SortableHeader
                    column="slaDays"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    SLA
                  </SortableHeader>

                  <SortableHeader
                    column="lastSyncedAt"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Terakhir Sinkron
                  </SortableHeader>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <LoadingRows />
                ) : (
                  sortedOrders.map((order) => {
                    const schedulable = isSchedulableOrder(order);

                    return (
                      <tr
                        aria-label={`Buka detail ${order.documentNumber || order.soNumber}`}
                        className="pending-order-row pending-order-row--clickable"
                        key={
                          order.documentKey ||
                          `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`
                        }
                        onClick={() => openOrderDetail(order)}
                        onKeyDown={(event) =>
                          openRowFromKeyboard(event, () => openOrderDetail(order))
                        }
                        tabIndex={0}
                      >
                        <td
                          className="selection-cell"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            aria-label={`Pilih ${order.documentNumber || order.soNumber}`}
                            checked={selectedIds.has(
                              order.documentKey ||
                                `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`,
                            )}
                            disabled={!schedulable}
                            onChange={() => toggleOrder(order)}
                            title={
                              schedulable
                                ? "Pilih untuk dijadwalkan"
                                : "Pesanan Diajukan belum bisa dijadwalkan"
                            }
                            type="checkbox"
                          />
                        </td>

                        <td>
                          <button
                            className="record-link record-link--button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openOrderDetail(order);
                            }}
                            type="button"
                          >
                            {order.documentNumber || order.soNumber}
                          </button>
                        </td>

                        <td>
                          {order.documentType === "SALES_ORDER" &&
                          order.approvalStatus === "APPROVED"
                            ? formatDate(order.approvedAt) || "Belum tercatat"
                            : formatDate(order.orderDate)}
                        </td>

                        <td>
                          <span className="customer-name">
                            {order.customerName}
                          </span>
                        </td>

                        <td>
                          {order.resolvedArea ||
                            order.billingCity ||
                            "Belum tersedia"}
                        </td>

                        <td>
                          <RouteBadge
                            color={order.resolvedRouteColor}
                            name={
                              order.resolvedRouteName ||
                              order.resolvedRouteCode ||
                              order.resolvedRoute
                            }
                          />
                        </td>

                        <td>{order.salesName || "—"}</td>

                        <td>
                          <ApprovalStatusBadge
                            label={order.approvalStatusLabel}
                            status={order.approvalStatus}
                          />
                        </td>

                        <td>
                          <ProcessingStatusBadge
                            label={order.processingStatusLabel}
                            status={order.processingStatus}
                          />
                        </td>

                        <td>
                          <PlanningStatusBadge status={order.planningStatus} />
                        </td>

                        <td>
                          <SlaBadge days={order.slaDays} />
                        </td>

                        <td>{formatTimestamp(order.lastSyncedAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {!loading && orders.length === 0 && (
              <div className="empty-state">
                <span>
                  <Icon name="orders" size={25} />
                </span>

                <h3>Belum ada pesanan yang perlu diproses</h3>

                <p>
                  Klik <strong>Sinkronkan Pesanan</strong> untuk mengambil
                  pesanan terbaru dari Accurate.
                </p>
              </div>
            )}
          </div>

          <footer className="records-panel__footer orders-data-surface__footer">
            <div className="orders-footer-context">
              <span>
                Halaman {pagination.page} dari {Math.max(pagination.pageCount, 1)}
              </span>
              <small>
                Pesanan Diajukan hanya untuk monitoring dan tidak dapat diproses
              </small>
            </div>

            <div className="pagination-controls">
              <span>
                {pagination.rowCount
                  ? `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(
                      pagination.page * pagination.pageSize,
                      pagination.rowCount,
                    )}`
                  : "0"}{" "}
                / {numberFormatter.format(pagination.rowCount)}
              </span>

              <button
                aria-label="Halaman sebelumnya"
                disabled={loading || pagination.page <= 1}
                onClick={() => changePage(pagination.page - 1)}
                type="button"
              >
                <Icon name="chevronLeft" size={16} />
              </button>

              <button
                aria-label="Halaman berikutnya"
                disabled={loading || pagination.page >= pagination.pageCount}
                onClick={() => changePage(pagination.page + 1)}
                type="button"
              >
                <Icon name="chevronRight" size={16} />
              </button>
            </div>
          </footer>
        </section>
        </>
        ) : (
          <ProcessedOrdersPanel />
        )}
      </section>

      {scheduleModalOpen && (
        <FleetOrderModal
          onCancel={() => setScheduleModalOpen(false)}
          onSuccess={handleScheduleSuccess}
          selectedDocuments={selectedOrders}
        />
      )}

      {detailOpen && (
        <OrderDetailModal
          detail={detailPayload}
          error={detailError}
          loading={detailLoading}
          onClearShippingAddress={
            clearCurrentOrderShippingAddress
          }
          onClose={closeOrderDetail}
          onRefresh={refreshCurrentOrderDetail}
          onSaveShippingAddress={
            saveCurrentOrderShippingAddress
          }
          refreshing={detailRefreshing}
          shippingError={shippingError}
          shippingMessage={shippingMessage}
          shippingSaving={shippingSaving}
        />
      )}
    </main>
  );
}
